import { useEffect, useState } from "react";
import { api } from "../../api";
import { Loader2, ImageOff, Paperclip, X } from "lucide-react";

const IMG_RE = /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i;

// Datei/Bild via Backend-Proxy (Token im Header, nicht in der URL) als Blob laden.
function useBlobUrl(filename, token) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!filename || !token) return undefined;
    let active = true;
    let obj = null;
    setUrl(null);
    setError(false);
    (async () => {
      try {
        const { data } = await api.get(`/magauth/files/${encodeURIComponent(filename)}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        });
        if (!active) return;
        obj = URL.createObjectURL(data);
        setUrl(obj);
      } catch {
        if (active) setError(true);
      }
    })();
    return () => {
      active = false;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [filename, token]);
  return { url, error };
}

function AttachmentThumb({ filename, token, onOpen }) {
  const isImage = IMG_RE.test(filename);
  const { url, error } = useBlobUrl(filename, token);
  const label = filename.replace(/^\d+_/, "");

  if (!isImage) {
    return (
      <a data-testid={`attachment-file-${filename}`} href={url || undefined}
        target="_blank" rel="noopener noreferrer" download={label}
        className="inline-flex items-center gap-1.5 rounded-lg text-xs font-semibold px-2.5 py-2"
        style={{ background: "#F1F5F9", color: "#475569", border: "1px solid #E2E8F0", textDecoration: "none", cursor: url ? "pointer" : "default", maxWidth: 180 }}>
        {url ? <Paperclip size={13} /> : <Loader2 size={13} className="animate-spin" />}
        <span className="truncate">{label}</span>
      </a>
    );
  }
  return (
    <button data-testid={`attachment-thumb-${filename}`} type="button"
      onClick={() => url && onOpen(url)}
      className="relative rounded-lg overflow-hidden flex items-center justify-center"
      style={{ width: 84, height: 84, background: "#F1F5F9", border: "1px solid #E2E8F0", cursor: url ? "zoom-in" : "default", flexShrink: 0, padding: 0 }}>
      {error ? <ImageOff size={20} color="#94A3B8" />
        : url ? <img src={url} alt="Anhang" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        : <Loader2 size={18} className="animate-spin" color="#94A3B8" />}
    </button>
  );
}

// Bild-/Anhang-Galerie unter einer Meldung. Klick auf ein Bild oeffnet die
// Grossansicht (Lightbox).
export function AttachmentGallery({ attachments, token, mid }) {
  const [lightbox, setLightbox] = useState(null);
  if (!attachments?.length) return null;
  return (
    <>
      <div className="flex flex-wrap gap-2 mt-3" data-testid={`meldung-attachments-${mid}`}>
        {attachments.map((f) => (
          <AttachmentThumb key={f} filename={f} token={token} onOpen={setLightbox} />
        ))}
      </div>
      {lightbox && (
        <div data-testid="attachment-lightbox" onClick={() => setLightbox(null)}
          className="fixed inset-0 flex items-center justify-center"
          style={{ background: "rgba(15,23,42,0.88)", zIndex: 9999, padding: 24 }}>
          <button data-testid="attachment-lightbox-close" onClick={() => setLightbox(null)}
            className="absolute flex items-center justify-center"
            style={{ top: 16, right: 16, background: "rgba(255,255,255,0.18)", border: "none", borderRadius: 9999, width: 40, height: 40, cursor: "pointer" }}>
            <X size={22} color="#fff" />
          </button>
          <img src={lightbox} alt="Anhang gross" onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 8 }} />
        </div>
      )}
    </>
  );
}
